import React, { useMemo, useState, useCallback } from 'react';
import EntityTable from 'src/components/BeaconV2Components/EntityTableRefresh/EntityTable';
import Pagination from 'src/components/BeaconV2Components/Pagination/Pagination';
import { AppTableColumnDef } from '@stackline/ui';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { Status } from '../ShelfPage/styles';

export interface BatchesEntityTableProps {
  batches: any[];
  isLoading: boolean;
  onBatchClick?: (batchId: string) => void;
  onDownload: (batch: any, idx: number) => void;
}

const getBatchId = (b: any, idx: number) => b?.id || b?.batchId || b?.crawlCampaignBatchId || b?.crawl_campaign_batch_id || idx;

const BatchesEntityTable: React.FC<BatchesEntityTableProps> = ({ batches, isLoading, onBatchClick, onDownload }) => {
  const getStartTimestamp = (batch: any): number => {
    const ts = batch?.startedAt || batch?.startTime || batch?.createdAt || null;
    return ts ? new Date(ts).getTime() : -Infinity;
  };

  const formatDuration = (batch: any): string => {
    // Prefer explicit completionTime - startTime if present
    const startRaw = batch?.startTime || batch?.startedAt || batch?.createdAt || null;
    const endRaw = batch?.completionTime || batch?.endedAt || batch?.endTime || null;
    let ms = 0;
    if (startRaw && endRaw) {
      const start = new Date(startRaw).getTime();
      const end = new Date(endRaw).getTime();
      ms = end - start;
    } else {
      // Fallbacks
      ms = (typeof batch?.durationMs === 'number' && batch.durationMs) ||
        (typeof batch?.durationMs === 'string' && Number(batch.durationMs)) ||
        (typeof batch?.duration === 'number' && batch.duration * 1000) ||
        0;
    }
    if (!ms || ms < 0) return '—';
    const minutes = Math.floor(ms / 60000);
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const rem = minutes % 60;
    return `${hours}h ${rem}m`;
  };

  const formatStart = (batch: any): string => {
    const ts = batch?.startedAt || batch?.startTime || batch?.createdAt || null;
    if (!ts) return '—';
    const d = new Date(ts);
    return d.toLocaleString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  const formatRows = (batch: any): string => {
    const statusRaw = (batch?.status || '').toString().toLowerCase();
    if (statusRaw === 'running') return '-';
    return (batch?.numRowsCollected ?? '—').toString();
  };

  const formatSuccessRate = (batch: any): string => {
    const val = batch?.successRate ?? batch?.successPercent ?? null;
    // If zero rows were collected, success rate should not show 100%
    const rowsCollected = Number(batch?.numRowsCollected ?? NaN);
    if (Number.isFinite(rowsCollected) && rowsCollected === 0) return '-';
    if (val == null) return '—';
    const pct = typeof val === 'number' ? (val > 1 ? val : val * 100) : Number(val);
    if (Number.isNaN(pct)) return '—';
    return `${Math.round(pct)}%`;
  };

  const formatStatus = (batch: any): string => {
    const s = (batch?.status || '').toString();
    return s ? s.charAt(0).toUpperCase() + s.slice(1) : '—';
  };

  const rows = useMemo(() => {
    return [...batches]
      .map((b, idx) => ({
        __raw: b,
        id: getBatchId(b, idx),
        start: formatStart(b),
        __ts: getStartTimestamp(b),
        duration: formatDuration(b),
        rows: formatRows(b),
        success: formatSuccessRate(b),
        status: formatStatus(b)
      }))
      .sort((a, b) => b.__ts - a.__ts);
  }, [batches]);

  const [sortField, setSortField] = useState<keyof typeof rows[number] | '__ts'>('__ts');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const toggleSort = useCallback((field: keyof typeof rows[number] | '__ts') => {
    setSortField((prev) => {
      if (prev === field) {
        setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
        return prev;
      }
      setSortDirection('asc');
      return field;
    });
  }, []);

  const arrowFor = (_field: string) => null;

  const renderSortableHeader = (label: string, field: keyof typeof rows[number] | '__ts') => (
    <span
      onClick={() => toggleSort(field)}
      style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, verticalAlign: 'middle' }}
      title={`Sort by ${label}`}
    >
      <span style={{ fontSize: 12, fontWeight: 700, lineHeight: '20px' }}>{label}</span>
      {/* sorting arrow removed by design */}
    </span>
  );

  const sortedRows = useMemo(() => {
    const arr = [...rows];
    const dir = sortDirection === 'asc' ? 1 : -1;
    const get = (r: any, f: any) => r[f];
    const compare = (a: any, b: any) => {
      const av = get(a, sortField);
      const bv = get(b, sortField);
      const an = typeof av === 'number' ? av : Number(av);
      const bn = typeof bv === 'number' ? bv : Number(bv);
      if (!Number.isNaN(an) && !Number.isNaN(bn)) return (an - bn) * dir;
      const as = (av ?? '').toString().toLowerCase();
      const bs = (bv ?? '').toString().toLowerCase();
      if (as < bs) return -1 * dir;
      if (as > bs) return 1 * dir;
      return 0;
    };
    return arr.sort(compare);
  }, [rows, sortField, sortDirection]);

  const columnDefs: (Omit<AppTableColumnDef, 'field'> & Pick<Partial<AppTableColumnDef>, 'field'>)[] = [
    {
      headerName: renderSortableHeader('Start Time', '__ts' as any) as any,
      field: 'start',
      tableColumnProps: { style: { width: '220px' } },
      valueFormatter: (value: string, row: any) => {
        const id = row?.id ?? row?.__raw?.id;
        const label = value || '—';
        return (
          <div aria-label={id != null ? `campaign-batch-id-${String(id)}` : undefined}>{label}</div>
        )
      }
    },
    { headerName: renderSortableHeader('Duration', 'duration' as any) as any, field: 'duration', tableColumnProps: { style: { width: '120px' } } },
    { headerName: renderSortableHeader('Rows Collected', 'rows' as any) as any, field: 'rows', tableColumnProps: { style: { width: '140px' } } },
    { headerName: renderSortableHeader('Success Rate', 'success' as any) as any, field: 'success', tableColumnProps: { style: { width: '120px' } } },
    {
      headerName: renderSortableHeader('Status', 'status' as any) as any,
      field: 'status',
      tableColumnProps: { style: { width: '120px' } },
      valueFormatter: (value: string, row: any) => {
        const raw = ((row?.__raw?.status ?? value) || '').toString().toLowerCase();
        if (raw === 'completed' || raw === 'complete') {
          return <Status variant={'enabled'}>Completed</Status> as any;
        }
        if (raw === 'failed' || raw === 'error' || raw === 'failure') {
          return <Status variant={'disabled'}>Failed</Status> as any;
        }
        const label = (value || raw || '—').toString();
        const pretty = label ? label.charAt(0).toUpperCase() + label.slice(1) : '—';
        return <Text variant="body2">{pretty}</Text> as any;
      }
    },
    {
      headerName: '',
      field: 'download',
      tableColumnProps: { style: { width: '120px' } },
      valueFormatter: (_: any, row: any, index?: number) => {
        const statusRaw = ((row?.status ?? row?.__raw?.status) || '').toString().toLowerCase();
        const canDownloadStatus = statusRaw === 'complete' || statusRaw === 'completed';
        const rowsCollected = Number(row?.numRowsCollected ?? row?.__raw?.numRowsCollected ?? NaN);
        const hasRows = Number.isFinite(rowsCollected) && rowsCollected > 0;
        const isDisabled = !(canDownloadStatus && hasRows);
        return (
          <button
            onClick={(e) => { if (isDisabled) return; e.stopPropagation(); onDownload(row.__raw, index || 0); }}
            disabled={isDisabled}
            style={{ fontSize: 12, padding: '6px 10px', borderRadius: 6, border: '1px solid #E6ECF2', background: isDisabled ? '#F5F8FB' : 'white', color: isDisabled ? '#9DA8B3' : '#052849', cursor: isDisabled ? 'not-allowed' : 'pointer' }}
          >
            Download
          </button>
        );
      }
    }
  ];

  const [page, setPage] = useState(1);
  const pageSize = 20;
  const totalPages = Math.max(Math.ceil(sortedRows.length / pageSize), 1);

  const onClickNext = () => setPage((p) => Math.min(totalPages, p + 1));
  const onClickPrevious = () => setPage((p) => Math.max(1, p - 1));

  return (
    <>
      <EntityTable
        isLoading={isLoading}
        rowData={sortedRows}
        columnDefs={columnDefs}
        shouldModifyColumnDefs={false}
        onRowClick={onBatchClick ? ((row: any, index?: number) => onBatchClick(String(getBatchId(row?.__raw || row, index || 0)))) as unknown as () => any : undefined}
        initialPageSize={pageSize}
        showPagination={false}
        page={page}
        setPage={setPage}
        appTableProps={{
          sx: {
            '& tbody tr': { cursor: onBatchClick ? 'pointer !important' : 'default !important' },
            '& thead th': { paddingTop: '10px !important', paddingBottom: '10px !important', height: '40px' }
          }
        }}
      />
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8, marginBottom: 32, paddingBottom: 16 }}>
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onClickNext={onClickNext}
          onClickPrevious={onClickPrevious}
          shouldUseInfinitePagination
        />
      </div>
    </>
  );
};

export default BatchesEntityTable;


